import React, { useEffect, useRef, useState } from "react";
import CrmLayout from "../../components/CrmLayout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Copy, ExternalLink, Wand2, Upload, Check, X, GripVertical, Lock } from "lucide-react";
import { brandCssVars } from "../../lib/format";

const COLOR_PRESETS = [
  { name: "Ocean Blue", hex: "#2563EB" },
  { name: "Sky", hex: "#0EA5E9" },
  { name: "Emerald", hex: "#059669" },
  { name: "Plum", hex: "#7C3AED" },
  { name: "Rose", hex: "#DB2777" },
  { name: "Sunset", hex: "#EA580C" },
  { name: "Ruby", hex: "#DC2626" },
  { name: "Ink", hex: "#0F172A" },
];

export default function Settings() {
  const { salon, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pullingColor, setPullingColor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const fileRef = useRef(null);
  const coverRef = useRef(null);
  const headerRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const bookingUrl = salon ? `${window.location.origin}/book/${salon.slug}` : "";

  const pullLogoColor = async () => {
    setPullingColor(true);
    try {
      const { data } = await api.post("/settings/logo-color", { logo_url: form.logo_url });
      set("brand_color", data.color);
      toast.success(`Accent color set from your logo (${data.color})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't read your logo"); }
    finally { setPullingColor(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo-upload", fd);
      set("logo_url", data.logo_url);
      if (data.color) set("brand_color", data.color);
      toast.success(data.color ? `Logo uploaded — accent color synced (${data.color})` : "Logo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/cover-upload", fd);
      set("cover_url", data.cover_url);
      toast.success("Cover photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingCover(false); if (coverRef.current) coverRef.current.value = ""; }
  };

  const uploadHeader = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHeader(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/cover-upload", fd);
      set("dashboard_header_url", data.cover_url);
      toast.success("Dashboard header uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingHeader(false); if (headerRef.current) headerRef.current.value = ""; }
  };

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/settings/gallery-upload", fd);
        setForm((f) => ({ ...f, gallery: [...(f.gallery || []), { url: data.url, caption: "" }].slice(0, 12) }));
      }
      toast.success("Photos added — save to publish");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingGallery(false); if (galleryRef.current) galleryRef.current.value = ""; }
  };
  const removeGalleryItem = (url) => setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((g) => (g.url || g) !== url) }));
  const setGalleryCaption = (url, caption) => setForm((f) => ({ ...f, gallery: (f.gallery || []).map((g) => ((g.url || g) === url ? { url, caption } : g)) }));
  const [dragIdx, setDragIdx] = useState(null);
  const moveGalleryItem = (from, to) => setForm((f) => {
    if (from == null || to == null || from === to) return f;
    const arr = [...(f.gallery || [])];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    return { ...f, gallery: arr };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        tagline: form.tagline || "", address: form.address || "", phone: form.phone || "",
        work_start: Number(form.work_start), work_end: Number(form.work_end),
        deposit_enabled: !!form.deposit_enabled, deposit_amount: Number(form.deposit_amount || 0),
        reminders_enabled: !!form.reminders_enabled, followups_enabled: !!form.followups_enabled,
        twilio_account_sid: form.twilio_account_sid || "", twilio_auth_token: form.twilio_auth_token || "",
        twilio_phone: form.twilio_phone || "",
        logo_url: form.logo_url || "", brand_color: form.brand_color || "", cover_url: form.cover_url || "",
        dashboard_header_url: form.dashboard_header_url || "",
        gallery: form.gallery || [],
        welcome_enabled: !!form.welcome_enabled, welcome_headline: form.welcome_headline || "", welcome_bio: form.welcome_bio || "",
        waitlist_offer_hours: Number(form.waitlist_offer_hours || 24),
        waitlist_max_wait_days: Number(form.waitlist_max_wait_days || 30),
        timezone: form.timezone || "UTC",
      });
      toast.success("Settings saved");
      await refresh();
      const { data } = await api.get("/settings"); setForm(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!form) return <CrmLayout title="Settings"><div className="text-muted-foreground">Loading…</div></CrmLayout>;

  return (
    <CrmLayout title="Settings" actions={<Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" data-testid="settings-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save changes"}</Button>}>
      <div className="max-w-3xl space-y-6">
        <Section title="Booking page">
          <div className="flex items-center gap-2 bg-secondary/50 rounded-md p-3 border border-border">
            <code className="text-sm flex-1 truncate" data-testid="booking-url">{bookingUrl}</code>
            <Button variant="ghost" size="sm" data-testid="copy-url" onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success("Link copied"); }}><Copy size={14} /></Button>
            <a href={bookingUrl} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><ExternalLink size={14} /></Button></a>
          </div>
          <div className="mt-4"><Label>Tagline</Label><Input className="mt-1" data-testid="set-tagline" value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="Your best hair day, every day" /></div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div><Label>Address</Label><Input className="mt-1" data-testid="set-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
            <div><Label>Phone</Label><Input className="mt-1" data-testid="set-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
        </Section>

        <Section title="Branding (your booking page)">
          <p className="text-sm text-muted-foreground mb-4">Personalize your public booking page with your logo, a cover photo and an accent color.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Logo</Label>
              <Input className="mt-1" data-testid="set-logo" value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="Paste a URL or upload below" />
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid="logo-file-input" onChange={uploadLogo} />
              <div className="flex items-center gap-4 mt-2">
                <button type="button" data-testid="upload-logo" disabled={uploading} onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline underline-offset-4 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload logo"}
                </button>
                <button type="button" data-testid="pull-logo-color" disabled={!form.logo_url || pullingColor} onClick={pullLogoColor}
                  className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline underline-offset-4 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
                  <Wand2 size={14} /> {pullingColor ? "Reading logo…" : "Use my logo's color"}
                </button>
                {form.logo_url && (
                  <button type="button" data-testid="remove-logo" onClick={() => { set("logo_url", ""); toast.success("Logo removed — save to apply"); }}
                    className="inline-flex items-center gap-1.5 text-sm text-destructive hover:underline underline-offset-4">
                    <X size={14} /> Remove logo
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">Uploading a logo auto-syncs your accent color.</p>
            </div>
            <div><Label>Cover photo</Label>
              <Input className="mt-1" data-testid="set-cover" value={form.cover_url || ""} onChange={(e) => set("cover_url", e.target.value)} placeholder="Paste a URL or upload below" />
              <input ref={coverRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="cover-file-input" onChange={uploadCover} />
              <div className="flex items-center gap-4 mt-2">
                <button type="button" data-testid="upload-cover" disabled={uploadingCover} onClick={() => coverRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline underline-offset-4 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
                  <Upload size={14} /> {uploadingCover ? "Uploading…" : "Upload cover"}
                </button>
                {form.cover_url && (
                  <button type="button" data-testid="remove-cover" onClick={() => { set("cover_url", ""); toast.success("Cover removed — save to apply"); }}
                    className="inline-flex items-center gap-1.5 text-sm text-destructive hover:underline underline-offset-4">
                    <X size={14} /> Remove cover
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">Shown as the banner on your public booking page.</p>
            </div>
            <div><Label>Dashboard header</Label>
              <Input className="mt-1" data-testid="set-dashboard-header" value={form.dashboard_header_url || ""} onChange={(e) => set("dashboard_header_url", e.target.value)} placeholder="Paste a URL or upload below" />
              <input ref={headerRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="header-file-input" onChange={uploadHeader} />
              <div className="flex items-center gap-4 mt-2">
                <button type="button" data-testid="upload-header" disabled={uploadingHeader} onClick={() => headerRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline underline-offset-4 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
                  <Upload size={14} /> {uploadingHeader ? "Uploading…" : "Upload header"}
                </button>
                {form.dashboard_header_url && (
                  <button type="button" data-testid="remove-header" onClick={() => { set("dashboard_header_url", ""); toast.success("Header removed — save to apply"); }}
                    className="inline-flex items-center gap-1.5 text-sm text-destructive hover:underline underline-offset-4">
                    <X size={14} /> Remove header
                  </button>
                )}
              </div>
              {form.dashboard_header_url && <img src={form.dashboard_header_url} alt="Dashboard header preview" className="mt-2 w-full h-24 object-cover rounded-md border border-border" />}
              <p className="text-xs text-muted-foreground mt-1.5">The banner shown across the top of your dashboard. Leave blank to use the default.</p>
            </div>
            <div>
              <Label>Accent color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" data-testid="set-color" value={form.brand_color || "#2563EB"} onChange={(e) => set("brand_color", e.target.value)} className="h-10 w-12 rounded border border-border bg-card cursor-pointer" />
                <Input className="flex-1" value={form.brand_color || ""} onChange={(e) => set("brand_color", e.target.value)} placeholder="#2563EB" />
              </div>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="color-presets">
                {COLOR_PRESETS.map((p) => {
                  const active = (form.brand_color || "").toUpperCase() === p.hex.toUpperCase();
                  return (
                    <button key={p.hex} type="button" title={p.name} aria-label={p.name} data-testid={`preset-${p.hex.replace("#", "").toLowerCase()}`}
                      onClick={() => set("brand_color", p.hex)}
                      className={`w-8 h-8 rounded-full border transition-transform hover:scale-110 flex items-center justify-center ${active ? "ring-2 ring-offset-2 ring-foreground/70 border-transparent" : "border-black/10"}`}
                      style={{ backgroundColor: p.hex }}>
                      {active && <Check size={14} className="text-white" />}
                    </button>
                  );
                })}
              </div>
              {form.brand_color && (
                <div data-testid="accent-workspace-note" className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                  <span className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: form.brand_color }} />
                  <span>This is your brand accent in the workspace — it also tints your dashboard.</span>
                </div>
              )}
            </div>
          </div>
          <div className="mt-5" data-testid="brand-preview" style={brandCssVars(form.brand_color)}>
            <div className="label-eyebrow text-muted-foreground mb-2">Live preview</div>
            <div className="rounded-lg overflow-hidden border border-border bg-card">
              <div className="relative h-24 bg-secondary" style={form.cover_url ? { backgroundImage: `url(${form.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                <div className="absolute inset-0 bg-black/40" />
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  {form.logo_url && <img src={form.logo_url} alt="logo" data-testid="brand-preview-logo" className="w-9 h-9 rounded-md object-cover bg-white" />}
                  <span className="text-white font-display font-bold">{form.name}</span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">1</span>
                  <span className="text-sm font-medium">Choose a service</span>
                </div>
                <div className="rounded-md border border-primary bg-primary/5 p-3 text-sm flex justify-between items-center mb-3">
                  <span className="font-medium">Women's Haircut</span><span className="font-display font-bold">$65</span>
                </div>
                <button type="button" tabIndex={-1} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-5 py-2 text-sm font-medium transition-colors">Continue</button>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Photo gallery" desc="Show off before & after work on your public booking page to win more bookings.">
          <input ref={galleryRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" data-testid="gallery-file-input" onChange={uploadGallery} />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="gallery-grid">
            {(form.gallery || []).map((g, idx) => {
              const url = g.url || g;
              return (
                <div key={url} className="space-y-1.5">
                  <div draggable data-testid="gallery-item"
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { moveGalleryItem(dragIdx, idx); setDragIdx(null); }}
                    onDragEnd={() => setDragIdx(null)}
                    className={`relative group aspect-square rounded-lg overflow-hidden border border-border cursor-move transition-opacity ${dragIdx === idx ? "opacity-40 ring-2 ring-accent" : ""}`}>
                    <img src={url} alt="Gallery" className="w-full h-full object-cover pointer-events-none" />
                    <div className="absolute top-1.5 left-1.5 w-6 h-6 rounded-md bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"><GripVertical size={13} /></div>
                    <button type="button" data-testid="remove-gallery-item" onClick={() => removeGalleryItem(url)}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={13} />
                    </button>
                  </div>
                  <Input data-testid="gallery-caption" value={g.caption || ""} maxLength={80} placeholder="Add a caption…"
                    onChange={(e) => setGalleryCaption(url, e.target.value)} className="h-8 text-xs" />
                </div>
              );
            })}
            {(form.gallery || []).length < 12 && (
              <button type="button" data-testid="upload-gallery" disabled={uploadingGallery} onClick={() => galleryRef.current?.click()}
                className="aspect-square rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-accent hover:border-accent transition-colors disabled:opacity-50">
                <Upload size={18} /> <span className="text-xs">{uploadingGallery ? "Uploading…" : "Add photos"}</span>
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Up to 12 square photos — drag to reorder, and add a short caption like "Balayage before & after".</p>
        </Section>

        <Section title="Welcome page">
          {form.tier === "premium" ? (
            <>
              <p className="text-sm text-muted-foreground mb-4">Greet clients with a branded welcome page before they book — tell your story with a headline, a short bio and your gallery photos.</p>
              <Row label="Show a welcome page" desc="Clients see this intro first, then tap “Book an appointment”.">
                <Switch checked={!!form.welcome_enabled} onCheckedChange={(v) => set("welcome_enabled", v)} data-testid="set-welcome-enabled" />
              </Row>
              {form.welcome_enabled && (
                <div className="mt-3 space-y-4">
                  <div><Label>Welcome headline</Label><Input className="mt-1" data-testid="set-welcome-headline" maxLength={80} value={form.welcome_headline || ""} onChange={(e) => set("welcome_headline", e.target.value)} placeholder="Welcome to Glamour Cuts" /></div>
                  <div><Label>About your salon</Label><Textarea className="mt-1" data-testid="set-welcome-bio" rows={5} maxLength={800} value={form.welcome_bio || ""} onChange={(e) => set("welcome_bio", e.target.value)} placeholder="Tell clients what makes your salon special — your vibe, your specialties, your story." /></div>
                  <p className="text-xs text-muted-foreground">Your cover photo, logo and gallery photos appear on the welcome page automatically.</p>
                </div>
              )}
            </>
          ) : (
            <div data-testid="welcome-locked" className="flex items-start gap-3 rounded-lg border border-accent/40 bg-accent/10 p-4">
              <Lock size={18} className="text-accent mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-medium">A Premium feature</div>
                <p className="text-sm text-muted-foreground mt-0.5">Add a fully customizable welcome page to your booking site — upgrade to Premium to greet clients with your story and photos before they book.</p>
                <a href="/billing" className="inline-block mt-2 text-sm text-accent hover:underline underline-offset-4" data-testid="welcome-upgrade-link">Upgrade to Premium →</a>
              </div>
            </div>
          )}
        </Section>

        <Section title="Working hours">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Opens at (hour)</Label><Input type="number" min="0" max="23" className="mt-1" data-testid="set-start" value={form.work_start} onChange={(e) => set("work_start", e.target.value)} /></div>
            <div><Label>Closes at (hour)</Label><Input type="number" min="1" max="24" className="mt-1" data-testid="set-end" value={form.work_end} onChange={(e) => set("work_end", e.target.value)} /></div>
          </div>
          <div className="mt-4">
            <Label>Time zone</Label>
            <select data-testid="set-timezone" value={form.timezone || "UTC"} onChange={(e) => set("timezone", e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-border bg-card px-3 text-sm">
              {["UTC","America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Phoenix","America/Toronto","America/Sao_Paulo","Europe/London","Europe/Paris","Europe/Berlin","Europe/Madrid","Europe/Istanbul","Asia/Dubai","Asia/Kolkata","Asia/Singapore","Asia/Tokyo","Australia/Sydney"].map((z) => <option key={z} value={z}>{z.replace(/_/g," ")}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1">Used to show waitlist clients exactly when their held spot expires.</p>
          </div>
        </Section>

        <Section title="Deposits">
          <Row label="Require a deposit to book" desc="Clients pay a card deposit to secure their slot.">
            <Switch checked={!!form.deposit_enabled} onCheckedChange={(v) => set("deposit_enabled", v)} data-testid="set-deposit-enabled" />
          </Row>
          {form.deposit_enabled && <div className="mt-3"><Label>Deposit amount ($)</Label><Input type="number" className="mt-1 max-w-[160px]" data-testid="set-deposit-amount" value={form.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} /></div>}
        </Section>

        <Section title="Notifications">
          <Row label="Send reminders" desc="Email/SMS the day before an appointment."><Switch checked={!!form.reminders_enabled} onCheckedChange={(v) => set("reminders_enabled", v)} data-testid="set-reminders" /></Row>
          <Row label="Send follow-ups" desc="Thank-you message after a completed visit."><Switch checked={!!form.followups_enabled} onCheckedChange={(v) => set("followups_enabled", v)} data-testid="set-followups" /></Row>
          <div className="mt-3"><Label>Waitlist offer expires after (hours)</Label><Input type="number" min="1" className="mt-1 max-w-[160px]" data-testid="set-offer-hours" value={form.waitlist_offer_hours ?? 24} onChange={(e) => set("waitlist_offer_hours", e.target.value)} /><p className="text-xs text-muted-foreground mt-1">If the offered client doesn't book in time, the slot rolls to the next waiting client.</p></div>
          <div className="mt-3"><Label>Auto-remove waitlist entries after (days)</Label><Input type="number" min="1" className="mt-1 max-w-[160px]" data-testid="set-max-wait-days" value={form.waitlist_max_wait_days ?? 30} onChange={(e) => set("waitlist_max_wait_days", e.target.value)} /><p className="text-xs text-muted-foreground mt-1">Stale or past-dated waiting clients expire automatically so they don't block fresh offers.</p></div>
        </Section>

        <Section title="SMS (Twilio)">
          <p className="text-sm text-muted-foreground mb-4">Add your own Twilio credentials to send SMS. {form.twilio_configured && <span className="text-[hsl(var(--success))]">Connected.</span>}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Account SID</Label><Input className="mt-1" data-testid="set-twilio-sid" value={form.twilio_account_sid || ""} onChange={(e) => set("twilio_account_sid", e.target.value)} placeholder="AC..." /></div>
            <div><Label>From phone</Label><Input className="mt-1" data-testid="set-twilio-phone" value={form.twilio_phone || ""} onChange={(e) => set("twilio_phone", e.target.value)} placeholder="+1..." /></div>
            <div className="sm:col-span-2"><Label>Auth token {form.twilio_configured && <span className="text-muted-foreground text-xs">(leave blank to keep)</span>}</Label><Input type="password" className="mt-1" data-testid="set-twilio-token" value={form.twilio_auth_token || ""} onChange={(e) => set("twilio_auth_token", e.target.value)} placeholder="••••••••" /></div>
          </div>
        </Section>
      </div>
    </CrmLayout>
  );
}

function Section({ title, children }) {
  return <div className="bg-card border border-border rounded-lg p-6"><h2 className="font-semibold mb-4">{title}</h2>{children}</div>;
}
function Row({ label, desc, children }) {
  return <div className="flex items-center justify-between py-2"><div><div className="text-sm font-medium">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>{children}</div>;
}
